import React, { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./DataTable.module.css";
import { bindControl } from "../Utils/sectionControlUtil";
import Button from "../InputControls/Button";
import _, { omit } from "lodash";

/**
 * TypeA
 * @type React.FC<TypeAPropTypes>
 */
const DataTableColumn = React.forwardRef((props, ref) => {
  const { dataSource, columns } = props;

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 가져오기 */
    getRows() {
      return _setComponentValues();
    },
  }));

  const refComponent = useRef({});

  /**
   * DataTableColumn Component의 State
   */
  const [currState, setCurrState] = useState({
    dataSource: dataSource,
    columns: columns,
  });

  //
  const _setRef = useCallback((element, idx, column) => {
    if (element) {
      if (!refComponent.current[column]) {
        refComponent.current[column] = [];
      }
      refComponent.current[column][idx] = element;
    }
  }, []);

  const _setComponentValues = () => {
    const newDataSource = _.cloneDeep(dataSource);
    const list = _removeRowSpanKeys(newDataSource);

    const columnList = [];
    const searchColumnBinding = (columnData) => {
      columnData.forEach((item) => {
        if (item.component) {
          columnList.push(item.binding);

          if (item.columns?.length) {
            searchColumnBinding(item.columns);
          }

          if (item.addComponent) {
            item.addComponent.add?.forEach((addItem) => {
              columnList.push(addItem.binding);
            });
          }
        } else {
          if (item.columns?.length) {
            searchColumnBinding(item.columns);
          }
        }
      });
    };

    searchColumnBinding(columns);

    columnList.forEach((name) => {
      if (refComponent.current[name]) {
        refComponent.current[name].forEach((ref, index) => {
          if (ref && list) {
            if (!list[index]) list[index] = {};
            list[index][name] = ref.getValue();
          }
        });
      }
    });

    // set state ( I dont't think it's necessary )
    setCurrState((prev) => ({ ...prev, dataSource: list }));

    return list;
  };

  // Remove keys ending with  _rowSpan(keyWord)
  const _removeRowSpanKeys = (arr) => {
    return arr.map((item) => {
      return Object.keys(item).reduce((acc, key) => {
        if (!key.endsWith("_rowSpan")) {
          acc[key] = item[key];
        }
        return acc;
      }, {});
    });
  };

  const _createComponent = (column, item, dataIndex) => {
    const component = bindControl(column.component.name, {
      controlOption: {
        ...omit(column.component, ["name", "binding", "label"]), // Exclude undefined props
        ...{ defaultValue: item[column.binding] },
      },
      ref: (el) => _setRef(el, dataIndex, column.binding),
    });

    let addComponent = null;

    if (column.addComponent?.add?.length) {
      addComponent = column.addComponent?.add?.map((x, idx) => {
        // check component create or remove at `addComponent`
        if (String(column.addComponent?.condition[x.binding + "_CHK"]) !== String(item[x.binding + "_CHK"])) {
          return null;
        }

        const rowKey = `DataTableColumn+component_${idx}_${Math.random()}`;

        // create `addComponent`
        return x.name === "btn" ? (
          <Button
            key={`${rowKey}_btn`}
            authBtn={"SEARCH"}
            gridIndex={{
              row: dataIndex ?? -1,
              col: columns.findIndex((item) => item.binding === column.binding) ?? -1,
              binding: column.binding,
            }}
            {...omit(x, ["name", "binding"])}
          />
        ) : (
          <React.Fragment key={`${rowKey}`}>
            {x.label ? <span>{x.label}</span> : null}
            {bindControl(x.name, {
              controlOption: {
                ...omit(x, ["name", "binding", "label"]), // Exclude undefined props
                ...{ defaultValue: item[x.binding] },
              },
              ref: (el) => _setRef(el, dataIndex, x.binding),
            })}
          </React.Fragment>
        );
      });
      addComponent = addComponent.filter(Boolean);
      if (!addComponent?.length) addComponent = null;
    }

    return (
      <>
        {component}
        {addComponent ? <div className={styles.tdFormTr}>{addComponent}</div> : null}
      </>
    );
  };

  // Transfer className
  const _createClassName = (classList) => {
    if (!Array.isArray(classList)) return `${styles[classList]}`;

    return classList
      ?.map((name) => {
        return `${styles[name]}`;
      })
      .join(" ");
  };

  // create <td>
  const _createTd = (item, column, dataIndex, columnIndex) => {
    const cellKey = `DataTableColumn+td_${columnIndex}_${Math.random()}_${column.binding}_${dataIndex}`;

    // when using the merge header, do not use child binding, use parent binding
    const excludeColumn = column.grouping ? column.columns.length : 0;
    // when `th : true` props exists, use the `th` tag otherwise use `td` tag
    const Tag = column.th ? "th" : "td";
    // when `className` props exists, create a style format and apply it to the `td` tag
    const className = column.className?.length ? _createClassName(column.className) : null;
    // when `iconClassName` props exists, create a style format and apply it to the child element of the `td` tag as using `span` tag
    const iconClassName = column.iconClassName?.length ? _createClassName(column.iconClassName) : null;

    // Keep the legacy innerHtml option name, but render as text to avoid HTML injection.
    const hasInnerHtmlText = !!column?.innerHtml;
    const innerHtmlText = hasInnerHtmlText ? item[column.binding] : null;

    return (item[`${column.binding}_rowSpan`] && column.binding) || (column.binding && excludeColumn) ? (
      <Tag
        key={`${cellKey}_${column.binding}_${dataIndex}`}
        rowSpan={item[`${column.binding}_rowSpan`]}
        colSpan={excludeColumn ? excludeColumn : 1}
        className={className ?? ""}
      >
        {hasInnerHtmlText ? <div className={"innerHtmlDiv"}>{innerHtmlText}</div> : null}
        {iconClassName && !hasInnerHtmlText ? <span className={iconClassName ?? ""} /> : null}
        {column.component && !hasInnerHtmlText
          ? _createComponent(column, item, dataIndex)
          : !hasInnerHtmlText
          ? item[column.binding]
          : null}
      </Tag>
    ) : column.columns ? (
      column.columns.map((childColumn) => {
        return _createTd(item, childColumn, dataIndex, columnIndex);
      })
    ) : null;
  };

  return dataSource
    ? dataSource.map((item, dataIndex) => {
        const rowKey = `DataTableColumn+tr_${dataIndex}_${Math.random()}`;

        return (
          <tr key={rowKey}>
            {columns.map((column, columnIndex) => {
              return _createTd(item, column, dataIndex, columnIndex);
            })}
          </tr>
        );
      })
    : null;
});

const DataTableColumnPropTypes = {
  columns: PropTypes.array,
  mergeColumns: PropTypes.array,
  dataSource: PropTypes.array,
};

DataTableColumn.propTypes = DataTableColumnPropTypes;
DataTableColumn.defaultProps = {
  columns: [],
  mergeColumns: [],
  dataSource: [],
};
DataTableColumn.displayName = "DataTableColumn";
export default DataTableColumn;
